import { useState } from "react";
import {
  Checkbox,
  FontIcon,
  Spinner,
  SpinnerSize,
  Text,
  TooltipHost,
  keyframes,
  mergeStyles,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { HorizontalStack, VerticalStack } from "../../Stack";
import { Document } from "../../../models";
import CustomDropdown from "../../CustomDropDown";
import WordDocIcon from "../../../assets/Icons/svg/wordDocIcon";
import PdfIconAlternative from "../../../assets/Icons/svg/pdfIconAlternative";
import { colorPallete } from "../../../assets/color";
import { OutlookAttachmentRow } from "./types";

const slideDownEnter = keyframes({
  "0%": { opacity: 0, transform: "translateY(-6px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const dropdownEnterStyle = mergeStyles({
  animation: `${slideDownEnter} 0.22s ease forwards`,
});

const smallDropdownStyle = mergeStyles({
  selectors: {
    " .dropDownText": { fontSize: "13px" },
    " .dropdownCard": { fontSize: "13px", minWidth: "unset" },
    " .versionText": { fontSize: "11px" },
    " .versionTextSelect": { fontSize: "11px" },
    " .dropDownContainer": { backgroundColor: "white" },
  },
});

const fileNameTextStyle = mergeStyles({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: "Poppins",
  color: "#1F2430",
  fontSize: "13px",
  fontWeight: "400",
  lineHeight: "18px",
  width: "100%",
});

const toggleLabelStyle = {
  fontFamily: "Poppins",
  fontSize: "10px",
  fontWeight: "500" as const,
  whiteSpace: "nowrap" as const,
  lineHeight: "14px",
};

const metaTextStyle = {
  fontFamily: "Poppins",
  fontSize: "11px",
  fontWeight: "400" as const,
  color: "#717070",
  whiteSpace: "nowrap" as const,
};

export function formatBytes(size?: number): string {
  if (size === undefined || size === null || Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentCardProps {
  row: OutlookAttachmentRow;
  docsData: Document[];
  // false while another import is running or when the version type forces new documents
  allowVersionToggle: boolean;
  isSubmitting: boolean;
  importedTooltip?: string;
  onSelectChange: (checked: boolean) => void;
  onNameSave: (name: string) => void;
  onTargetChange: (
    isNewVersion: boolean,
    rootDocumentId?: number,
    documentFamilyName?: string
  ) => void;
  onRetry: () => void;
}

const AttachmentCard: React.FC<AttachmentCardProps> = ({
  row,
  docsData,
  allowVersionToggle,
  isSubmitting,
  importedTooltip,
  onSelectChange,
  onNameSave,
  onTargetChange,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(row.newName);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedDocItem, setSelectedDocItem] = useState<Document | null>(null);

  const { attachment } = row;
  const isWordDoc = attachment.name.toLowerCase().includes(".doc");
  const locked =
    row.imported || row.status === "done" || row.status === "importing";
  const interactable = !locked && !isSubmitting && row.isSupported;

  const handleSetDocumentItem = (item: Document | null) => {
    setSelectedDocItem(item);
    if (item) {
      onTargetChange(
        true,
        item.rootDocumentId,
        item.rootDocumentName?.split(".docx")[0] ?? ""
      );
    }
  };

  const handleToggle = (toNewVersion: boolean) => {
    if (!interactable || toNewVersion === row.isNewVersion) return;
    if (!toNewVersion) {
      setSelectedDocItem(null);
    }
    onTargetChange(toNewVersion, undefined, undefined);
  };

  const saveName = () => {
    setIsEditing(false);
    onNameSave(editValue);
  };

  const statusIndicator = () => {
    if (row.imported || row.status === "done") {
      return (
        <TooltipHost content={importedTooltip ?? String(t("outlook_already_imported"))}>
          <HorizontalStack verticalAlign="center" style={{ gap: "4px" }}>
            <FontIcon
              iconName="SkypeCircleCheck"
              style={{ fontSize: "16px", color: colorPallete.btGreen }}
            />
            <Text style={metaTextStyle}>
              {row.status === "done"
                ? t("outlook_import_done")
                : t("outlook_already_imported")}
            </Text>
          </HorizontalStack>
        </TooltipHost>
      );
    }
    if (row.status === "importing") {
      return (
        <HorizontalStack verticalAlign="center" style={{ gap: "4px" }}>
          <Spinner size={SpinnerSize.xSmall} />
          <Text style={metaTextStyle}>{t("outlook_importing")}</Text>
        </HorizontalStack>
      );
    }
    if (row.status === "failed") {
      return (
        <HorizontalStack verticalAlign="center" style={{ gap: "6px" }}>
          <TooltipHost content={row.error ?? String(t("outlook_import_failed"))}>
            <FontIcon
              iconName="StatusErrorFull"
              style={{ fontSize: "16px", color: colorPallete.btRed }}
            />
          </TooltipHost>
          <Text
            style={{
              ...metaTextStyle,
              color: colorPallete.btBlue,
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={onRetry}
          >
            {t("outlook_retry")}
          </Text>
        </HorizontalStack>
      );
    }
    if (!row.isSupported) {
      return (
        <TooltipHost content={String(t("outlook_unsupported_type"))}>
          <Text style={metaTextStyle}>{t("outlook_unsupported")}</Text>
        </TooltipHost>
      );
    }
    return null;
  };

  return (
    <VerticalStack
      style={{
        border: "1px solid",
        borderRadius: "5px",
        borderColor: row.isInvalid ? "red" : "#D8E1EB",
        width: "100%",
        padding: row.isNewVersion ? "10px" : "5px 10px",
        gap: row.isNewVersion ? "4px" : "0px",
        background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
        position: "relative",
        zIndex: isDropdownOpen ? 10 : "auto",
        opacity: row.imported || !row.isSupported ? 0.65 : 1,
      }}
    >
      {/* Row 1: checkbox + icon + filename + toggle + status */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto minmax(0, 1fr) auto auto",
          alignItems: "center",
          minHeight: "32px",
          gap: "6px",
          width: "100%",
          padding: "5px 10px",
          borderRadius: "5px",
          background: row.isNewVersion
            ? "#fff"
            : "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
        }}
      >
        <Checkbox
          checked={row.selected}
          disabled={!interactable}
          onChange={(_ev, checked) => onSelectChange(!!checked)}
          styles={{ root: { marginRight: "2px" } }}
        />
        <div style={{ flexShrink: 0 }}>
          {isWordDoc ? <WordDocIcon /> : <PdfIconAlternative />}
        </div>
        <VerticalStack style={{ minWidth: 0 }}>
          {isEditing ? (
            <input
              title={editValue}
              type="text"
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
              onBlur={saveName}
              onKeyDown={(event) => {
                if (event.key === "Enter") saveName();
              }}
              style={{
                fontFamily: "Poppins",
                width: "95%",
                minWidth: 0,
                boxSizing: "border-box",
                fontSize: "13px",
                backgroundColor: "white",
                paddingRight: "5px",
              }}
              autoFocus={true}
            />
          ) : (
            <Text
              block
              className={fileNameTextStyle}
              title={row.newName}
              onClick={() => {
                if (interactable && !row.isNewVersion) {
                  setEditValue(row.newName);
                  setIsEditing(true);
                }
              }}
            >
              {row.newName}
            </Text>
          )}
          {!!formatBytes(attachment.size) && (
            <Text style={metaTextStyle}>{formatBytes(attachment.size)}</Text>
          )}
        </VerticalStack>
        {allowVersionToggle && interactable ? (
          <HorizontalStack
            verticalAlign="center"
            style={{ gap: "6px", flexShrink: 0 }}
          >
            <div
              onClick={() => handleToggle(!row.isNewVersion)}
              style={{
                position: "relative",
                width: "36px",
                height: "18px",
                borderRadius: "50px",
                flexShrink: 0,
                cursor: "pointer",
                backgroundColor: colorPallete.btBlue,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "2px",
                  left: "2px",
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  transform: row.isNewVersion
                    ? "translateX(18px)"
                    : "translateX(0)",
                  transition: "transform 0.2s ease",
                }}
              />
            </div>
            <Text style={{ ...toggleLabelStyle, color: "#1F2430" }}>
              {row.isNewVersion ? t("new_version") : t("new_document")}
            </Text>
          </HorizontalStack>
        ) : (
          <div />
        )}
        <div style={{ flexShrink: 0 }}>{statusIndicator()}</div>
      </div>

      {/* Row 2: document family dropdown */}
      {row.isNewVersion && interactable && (
        <div className={dropdownEnterStyle}>
          <VerticalStack
            className={smallDropdownStyle}
            style={{ paddingBottom: "4px" }}
          >
            <span
              style={{
                fontFamily: "Poppins",
                color: "#1F2430",
                margin: "4px 0 4px",
              }}
            >
              {t("document_family")}:
            </span>
            <CustomDropdown
              options={docsData}
              title={t("document_dd")}
              isDocumentDropdown={true}
              setDocumentItem={handleSetDocumentItem}
              documentItem={selectedDocItem}
              rootDocumentId={row.rootDocumentId ?? 0}
              onOpenChange={setIsDropdownOpen}
            />
          </VerticalStack>
        </div>
      )}
    </VerticalStack>
  );
};

export default AttachmentCard;
